import { api } from '../../lib/api';
import { pollUntil, uploadDirect, type PresignedTarget, type UploadOptions } from '../../lib/upload';

/**
 * Where the studio publishes to.
 *
 * `path` is public - it is the host's username, and it is also where listeners
 * fetch from. `key` is not: it is write access to somebody's broadcast, and it
 * appears only inside `whip`. See `App\Domain\Station\StreamIngest` for why the
 * two are separate.
 *
 * Every one of these endpoints is behind the `studio` feature flag and answers
 * 404 when it is off, so a client that renders the studio anyway gets a 404
 * rather than a half-working broadcast.
 */
export interface StreamCredentials {
  path: string;
  key: string;
  /** POST an SDP offer here. Carries the key. */
  whip: string;
  /** What listeners fetch over WebRTC, for a studio broadcast. */
  playback: string;
  /**
   * What an RTMP broadcast is watched through, host and listener alike.
   *
   * Present here as well as on the room because the console previews the
   * encoder before going on air, and the room carries no playback URL until it
   * is live.
   */
  hls: string;
  /**
   * The same input, for an encoder that is not a browser.
   *
   * `url` is the server and `key` is the credential; OBS wants them in two
   * separate fields, which is also how they must be treated - the key alone is
   * permission to broadcast as this station.
   */
  rtmps: { url: string; key: string };
}

/** Whether an encoder is currently publishing to this station's input. */
export interface IngestStatus {
  connected: boolean;
  /** `rtmps`, `webrtc`, `srt` - or null before anything has ever connected. */
  protocol: string | null;
  since: string | null;
}

export const studio = {
  credentials: () => api.get<StreamCredentials>('/me/station/stream'),
  /** Invalidate the current key and mint a new one. */
  rotate: () => api.delete<StreamCredentials>('/me/station/stream'),
  /**
   * Safe to poll, unlike `credentials` - it carries no secret.
   *
   * That separation is the reason there are two routes rather than one with
   * more fields: a console checking every five seconds whether OBS has
   * connected would otherwise be putting a broadcast credential on the wire
   * hundreds of times a session.
   */
  status: () => api.get<IngestStatus>('/me/station/stream/status'),
};

/**
 * An image the host has uploaded to put on their broadcast.
 *
 * Owned by the person rather than by a scene, so one logo serves every
 * arrangement they build. `width` and `height` are here because a layer needs
 * them to arrive at its own aspect ratio rather than stretched into whatever
 * rectangle the code guessed.
 */
export interface Overlay {
  id: number;
  label: string;
  /** Loadable CORS-clean, which is the only kind the canvas can draw. */
  url: string;
  width: number;
  height: number;
  created_at: string;
}

export const overlays = {
  list: () =>
    api.get<{ overlays: Overlay[]; limit: number }>('/me/studio/overlays'),

  remove: (id: number) => api.delete(`/me/studio/overlays/${id}`),

  /**
   * Upload a picture and wait for it to be usable.
   *
   * ## Why this waits rather than returning immediately
   *
   * The bytes go straight to storage and the API only queues the work, so the
   * moment the PUT finishes there is nothing to draw yet - `ProcessOverlay` has
   * to resize and publish it first. Returning here would hand the caller an
   * overlay that does not exist, and the host would watch a layer they added
   * fail to appear.
   *
   * So it polls the library until the row shows up. The row is written last, by
   * the job, after the object is in the bucket - which makes "it is in the
   * list" exactly equivalent to "it can be drawn".
   *
   * Resolves null if the worker has not finished within the timeout. That is
   * not a failure: the upload landed and the overlay will appear on its own.
   * The caller says "still processing" rather than "something went wrong".
   */
  async upload(file: File, options: UploadOptions = {}): Promise<Overlay | null> {
    const before = await overlays.list();
    const known = new Set(before.overlays.map((o) => o.id));

    const target = await api.post<PresignedTarget>('/me/studio/overlays/init', {
      content_type: file.type,
      size_bytes: file.size,
    });

    await uploadDirect(target, file, options);

    await api.post('/me/studio/overlays/complete', {
      key: target.key,
      // The filename, so a host with three logos can tell them apart. Trimmed
      // of its extension, which tells them nothing they cannot see.
      label: file.name.replace(/\.[^.]+$/, '').slice(0, 80) || 'Overlay',
    });

    let added: Overlay | null = null;

    await pollUntil(
      () => overlays.list(),
      (result) => {
        added = result.overlays.find((o) => !known.has(o.id)) ?? null;

        return added !== null;
      },
      { intervalMs: 800, timeoutMs: 30_000 },
    );

    return added;
  },
};
